import * as React from "react";
import {
  BarChartCard,
  BarList,
  CHART_COLORS,
  Dialog,
  DonutChart,
  EmptyState,
  FunnelBars,
  LineChartCard,
  PageHeader,
  Skeleton,
  SkeletonStat,
  Spinner,
  TrendStat,
  cn,
  formatDateTimeVn,
  formatDayShort,
  formatVnd,
  formatVndCompact,
} from "@chotca/ui";
import {
  useAdminAnalyticsFunnel,
  useAdminAnalyticsRevenue,
  useAdminAnalyticsSummary,
  useAdminAnalyticsTimeseries,
  useAdminSubscriptionStats,
  useProSourceOrgs,
} from "@chotca/api-client";
import { Info } from "lucide-react";
import { useTableFilters } from "@/lib/use-table-filters";

const RANGE_OPTIONS = [
  { value: "today", label: "Hôm nay" },
  { value: "7d", label: "7 ngày" },
  { value: "30d", label: "30 ngày" },
  { value: "90d", label: "90 ngày" },
] as const;

type RangeValue = (typeof RANGE_OPTIONS)[number]["value"];

// Turn a preset into a concrete [from, to) UTC window. Computed once per range
// value (memoized below) so the query key stays stable across renders.
function rangeToWindow(range: RangeValue): { from: string; to: string } {
  const now = new Date();
  if (range === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: now.toISOString() };
  }
  const days = range === "7d" ? 7 : range === "90d" ? 90 : 30;
  const from = new Date(now.getTime() - days * 24 * 3600 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

export function DashboardClient() {
  const filters = useTableFilters({ range: {} });
  const range = (filters.applied.range as RangeValue) || "30d";
  const window = React.useMemo(() => rangeToWindow(range), [range]);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Tổng quan"
        description="Số liệu vận hành Chốt Ca theo thời gian."
        actions={
          <RangePicker
            value={range}
            onChange={(v) => filters.set("range", v)}
          />
        }
      />

      <SummarySection range={window} />
      <SubscriptionSection />
      <GrowthSection range={window} />
      <ActivitySection range={window} />
      <RevenueSection range={window} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscription stats — active orgs per plan + plan revenue (M2)
// ---------------------------------------------------------------------------
function SubscriptionSection() {
  const { data, isLoading } = useAdminSubscriptionStats();
  // Which Pro-provenance drill-down is open (null = closed).
  const [drill, setDrill] = React.useState<"GIFT" | "PURCHASE" | null>(null);

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-bold uppercase text-ash">Gói đăng ký</h2>
      {isLoading || !data ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-2xl border border-smoke bg-mist/40"
            />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <PlanStat label="Tổng doanh nghiệp" value={data.totalOrgs} />
            <PlanStat label="Đang dùng Free" value={data.activeByPlan.FREE} />
            <PlanStat
              label="Đang dùng Pro"
              value={data.activeByPlan.PRO}
              accent
            />
            <PlanStat
              label="Đang dùng Business"
              value={data.activeByPlan.BUSINESS}
              accent
            />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <PlanStat
              label="Pro từ mã tặng"
              value={data.proBySource.gift}
              onInfo={() => setDrill("GIFT")}
            />
            <PlanStat
              label="Pro từ mua"
              value={data.proBySource.purchase}
              onInfo={() => setDrill("PURCHASE")}
            />
          </div>
        </>
      )}

      <ProSourceDialog source={drill} onClose={() => setDrill(null)} />
    </section>
  );
}

function PlanStat({
  label,
  value,
  accent,
  onInfo,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  // When set, renders an (i) button in the corner that opens a drill-down.
  onInfo?: () => void;
}) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border p-4",
        accent ? "border-lime/40 bg-lime/10" : "border-smoke bg-paper",
      )}
    >
      {onInfo ? (
        <button
          type="button"
          onClick={onInfo}
          aria-label={`Xem danh sách: ${label}`}
          className="absolute right-2 top-2 rounded-full p-1 text-ash transition-colors hover:bg-mist hover:text-ink"
        >
          <Info size={15} />
        </button>
      ) : null}
      <p className="pr-6 text-[11px] font-semibold uppercase text-ash">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold text-ink">{value}</p>
    </div>
  );
}

// The (i) drill-down: lists the orgs behind the "Pro từ mã / từ mua" numbers.
// The split is by the subscription's current source (last activation wins), so
// each active-Pro org is in exactly one list.
function ProSourceDialog({
  source,
  onClose,
}: {
  source: "GIFT" | "PURCHASE" | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useProSourceOrgs(source);
  const title =
    source === "GIFT"
      ? "Doanh nghiệp Pro từ mã tặng"
      : "Doanh nghiệp Pro từ mua";
  const items = data?.items ?? [];

  return (
    <Dialog open={source != null} onClose={onClose} title={title}>
      <div className="space-y-3">
        <p className="text-xs text-ash">
          Doanh nghiệp đang dùng gói Pro còn hạn, lần kích hoạt gần nhất là{" "}
          {source === "GIFT" ? "từ mã quà tặng" : "từ đơn mua"}. Nếu doanh
          nghiệp mua thêm sau khi nhận quà (hoặc ngược lại) thì tính theo lần
          gần nhất.
        </p>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Chưa có doanh nghiệp nào" />
        ) : (
          <div className="max-h-80 divide-y divide-smoke overflow-y-auto rounded-md border border-line">
            {items.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="line-clamp-1 text-sm font-medium text-ink">
                  {o.name}
                </span>
                <span className="shrink-0 text-xs text-ash">
                  {o.expiresAt
                    ? `HH ${formatDateTimeVn(o.expiresAt)}`
                    : "không hết hạn"}
                </span>
              </div>
            ))}
          </div>
        )}
        {items.length > 0 ? (
          <p className="text-right text-xs text-ash">
            {items.length} doanh nghiệp
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Range picker (segmented control, persisted in the URL via useTableFilters)
// ---------------------------------------------------------------------------
function RangePicker({
  value,
  onChange,
}: {
  value: RangeValue;
  onChange: (v: RangeValue) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-smoke bg-paper p-0.5">
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={cn(
            "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
            value === opt.value
              ? "bg-ink text-paper"
              : "text-ash hover:text-ink",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 inline-flex items-center gap-2 text-sm uppercase tracking-wide text-ink/60">
      <span aria-hidden className="h-2 w-2 rounded-full bg-lime" />
      {children}
    </h2>
  );
}

// ---------------------------------------------------------------------------
// Summary KPI strip
// ---------------------------------------------------------------------------
type Range = { from: string; to: string };

function SummarySection({ range }: { range: Range }) {
  const { data, isLoading } = useAdminAnalyticsSummary(range);

  if (isLoading || !data) {
    return (
      <section>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonStat key={i} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <TrendStat
          label="User mới"
          value={data.newUsers.value.toLocaleString("vi-VN")}
          deltaPct={data.newUsers.deltaPct}
          accent
        />
        <TrendStat
          label="Tin mới"
          value={data.newPostings.value.toLocaleString("vi-VN")}
          deltaPct={data.newPostings.deltaPct}
          accent
        />
        <TrendStat
          label="Ứng tuyển"
          value={data.applications.value.toLocaleString("vi-VN")}
          deltaPct={data.applications.deltaPct}
          accent
        />
        <TrendStat
          label="Liên hệ đã chốt"
          value={data.invitesCommitted.value.toLocaleString("vi-VN")}
          deltaPct={data.invitesCommitted.deltaPct}
          accent
        />
        <TrendStat
          label="Doanh thu"
          value={formatVnd(data.revenueVnd.value)}
          deltaPct={data.revenueVnd.deltaPct}
        />
        <TrendStat
          label="DN có trả tiền"
          value={data.payingOrgs.value.toLocaleString("vi-VN")}
          deltaPct={data.payingOrgs.deltaPct}
        />
        <TrendStat
          label="Tỉ lệ thanh toán"
          value={`${Math.round(data.paymentSuccessRate.value * 100)}%`}
          deltaPct={data.paymentSuccessRate.deltaPct}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Growth
// ---------------------------------------------------------------------------
function GrowthSection({ range }: { range: Range }) {
  const { data, isLoading } = useAdminAnalyticsTimeseries(range);
  const points = data?.points ?? [];

  return (
    <section>
      <SectionTitle>Tăng trưởng</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartSkeletonGate loading={isLoading}>
          <LineChartCard
            title="Người dùng mới"
            subtitle="Theo ngày, tách người tìm việc và nhà tuyển dụng"
            data={points}
            xKey="date"
            xTickFormatter={formatDayShort}
            series={[
              {
                key: "newWorkers",
                label: "Người tìm việc",
                color: CHART_COLORS.primary,
              },
              {
                key: "newEmployers",
                label: "Nhà tuyển dụng",
                color: CHART_COLORS.info,
              },
            ]}
          />
        </ChartSkeletonGate>
        <ChartSkeletonGate loading={isLoading}>
          <LineChartCard
            title="Tin & doanh nghiệp mới"
            subtitle="Tin tuyển dụng và doanh nghiệp tạo mới mỗi ngày"
            data={points}
            xKey="date"
            xTickFormatter={formatDayShort}
            series={[
              {
                key: "newPostings",
                label: "Tin tuyển dụng",
                color: CHART_COLORS.ember,
              },
              {
                key: "newOrgs",
                label: "Doanh nghiệp",
                color: CHART_COLORS.info,
              },
            ]}
          />
        </ChartSkeletonGate>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Activity (marketplace funnel)
// ---------------------------------------------------------------------------
function ActivitySection({ range }: { range: Range }) {
  const series = useAdminAnalyticsTimeseries(range);
  const funnelQ = useAdminAnalyticsFunnel(range);
  const points = series.data?.points ?? [];
  const funnel = funnelQ.data;

  const funnelStages = funnel
    ? [
        { label: "Ứng tuyển", value: funnel.application.applied },
        { label: "Được mời", value: funnel.application.invited },
        { label: "Nhận lịch", value: funnel.application.accepted },
        { label: "Chốt liên hệ", value: funnel.application.committed },
      ]
    : [];

  const inviteStatusItems = funnel
    ? [
        { label: "Chờ phản hồi", value: funnel.inviteStatus.LOCKED },
        { label: "Đã nhận lịch", value: funnel.inviteStatus.ACCEPTED },
        {
          label: "Đã chốt",
          value: funnel.inviteStatus.COMMITTED,
          color: CHART_COLORS.positive,
        },
        { label: "Từ chối", value: funnel.inviteStatus.REJECTED },
        { label: "Hết hạn", value: funnel.inviteStatus.EXPIRED },
        { label: "Đã hủy", value: funnel.inviteStatus.CANCELLED },
      ]
    : [];

  return (
    <section>
      <SectionTitle>Hoạt động</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartSkeletonGate loading={series.isLoading} className="lg:col-span-2">
          <LineChartCard
            title="Ứng tuyển & chốt liên hệ"
            subtitle="Lượt ứng tuyển, ứng viên do nhà tuyển dụng tìm, và liên hệ đã chốt"
            data={points}
            xKey="date"
            xTickFormatter={formatDayShort}
            series={[
              {
                key: "applications",
                label: "Ứng tuyển",
                color: CHART_COLORS.primary,
              },
              {
                key: "applicationsSourced",
                label: "DN chủ động tìm",
                color: CHART_COLORS.sun,
              },
              {
                key: "invitesCommitted",
                label: "Liên hệ đã chốt",
                color: CHART_COLORS.info,
              },
            ]}
          />
        </ChartSkeletonGate>
        <ChartSkeletonGate loading={funnelQ.isLoading}>
          <FunnelBars
            title="Phễu ứng tuyển"
            subtitle={
              funnel
                ? `Tỉ lệ chốt lời mời ${Math.round(funnel.commitRate * 100)}%`
                : undefined
            }
            stages={funnelStages}
          />
        </ChartSkeletonGate>
        <ChartSkeletonGate loading={funnelQ.isLoading}>
          <BarList
            title="Trạng thái lời mời"
            subtitle={
              funnel
                ? `Tỉ lệ lấp đầy tin ${Math.round(funnel.postings.fillRate * 100)}% (${funnel.postings.filled}/${funnel.postings.created})`
                : undefined
            }
            items={inviteStatusItems}
          />
        </ChartSkeletonGate>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Revenue
// ---------------------------------------------------------------------------
function RevenueSection({ range }: { range: Range }) {
  const { data, isLoading } = useAdminAnalyticsRevenue(range);

  const statusSlices = data
    ? [
        {
          label: "Đã thanh toán",
          value: data.statusCounts.PAID,
          color: CHART_COLORS.positive,
        },
        {
          label: "Chờ thanh toán",
          value: data.statusCounts.PENDING,
          color: CHART_COLORS.sun,
        },
        {
          label: "Hết hạn",
          value: data.statusCounts.EXPIRED,
          color: CHART_COLORS.ash,
        },
        {
          label: "Thất bại",
          value: data.statusCounts.FAILED,
          color: CHART_COLORS.danger,
        },
      ]
    : [];

  const packItems = (data?.byPack ?? []).map((p) => ({
    label: p.label,
    value: p.revenueVnd,
    hint: `${p.orders} đơn`,
  }));

  return (
    <section>
      <SectionTitle>Doanh thu</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartSkeletonGate loading={isLoading} className="lg:col-span-2">
          <BarChartCard
            title="Doanh thu theo ngày"
            subtitle="Tổng tiền các đơn mua lượt liên hệ đã thanh toán"
            data={data?.series ?? []}
            xKey="date"
            xTickFormatter={formatDayShort}
            valueFormatter={formatVndCompact}
            showLegend={false}
            series={[
              {
                key: "revenueVnd",
                label: "Doanh thu",
                color: CHART_COLORS.primary,
              },
            ]}
          />
        </ChartSkeletonGate>
        <ChartSkeletonGate loading={isLoading}>
          <DonutChart
            title="Trạng thái thanh toán"
            subtitle="Tỉ lệ đơn thành công trên tổng số đơn"
            data={statusSlices}
            centerValue={
              data ? `${Math.round(data.paymentSuccessRate * 100)}%` : "0%"
            }
            centerLabel="thành công"
            className="h-full"
          />
        </ChartSkeletonGate>
        <ChartSkeletonGate loading={isLoading}>
          <BarList
            title="Doanh thu theo gói"
            subtitle="Xếp theo doanh thu đã nhận"
            items={packItems}
            valueFormatter={formatVndCompact}
            emptyText="Chưa có đơn nào trong kỳ"
            className="h-full"
          />
        </ChartSkeletonGate>
      </div>
    </section>
  );
}

// Renders a card-sized skeleton while a chart's data loads, then its children.
function ChartSkeletonGate({
  loading,
  className,
  children,
}: {
  loading: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className={className}>
        <Skeleton className="h-[288px] w-full rounded-2xl" />
      </div>
    );
  }
  // h-full so grid siblings stretch to equal height (align-items: stretch);
  // the child chart card also needs h-full to fill this wrapper.
  return <div className={cn("h-full", className)}>{children}</div>;
}
